// External libraries
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';

// Zocdoc dependencies
import { Link } from '@zocdoc-frontend-common/components-link';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { white } from '@zocdoc/branding/colors';
import PaddedContainer from '@zocdoc-frontend-common/components-padded-container';

/*
    Sitemap renders as a collapsed area on the bottom of the footer
    containing links to directories of profiles and landing pages.
    The objective is to minimize the link depth of these pages
    which improves SEO.

    Sitemap should always render to the DOM, even if hidden, 
    so simple html crawlers can read the links without running javascript.

    For now, we are maintaining the number of pages manually by modifying
    the PROFILE_COUNT and LANDING_PAGE_COUNT constants below.
    See https://github.com/Zocdoc/seo-directory-webapp for more information.
*/
const DEFAULT_PROFILE_COUNT = 76;
const DEFAULT_LANDING_PAGE_COUNT = 275;

class Sitemap extends PureComponent {
    static propTypes = {
        profileCount: PropTypes.number,
        landingPageCount: PropTypes.number,
        isVisible: PropTypes.bool,
    };

    static defaultProps = {
        profileCount: DEFAULT_PROFILE_COUNT,
        landingPageCount: DEFAULT_LANDING_PAGE_COUNT,
        isVisible: false,
    };

    render() {
        const profileIndexes = [];
        for (let i = 0; i < this.props.profileCount; ++i) {
            profileIndexes[i] = i;
        }

        const landingPageIndexes = [];
        for (let i = 0; i < this.props.landingPageCount; ++i) {
            landingPageIndexes[i] = i;
        }

        return (
            <SitemapContainer
                data-test="footer-sitemap"
                isVisible={this.props.isVisible}
            >
                <StyledLinkGroupHeader>
                    <StyledLinkGroupHeaderLink href="/profiles" target="_blank">
                        Doctor profiles
                    </StyledLinkGroupHeaderLink>
                </StyledLinkGroupHeader>

                <StyledLinkGroup>
                    {profileIndexes.map(i => (
                        <StyledLink key={i} href={`/profiles/${i}`}>
                            {`${i} `}
                        </StyledLink>
                    ))}
                </StyledLinkGroup>

                <StyledLinkGroupHeader>
                    <StyledLinkGroupHeaderLink href="/listings" target="_blank">
                        Landing pages
                    </StyledLinkGroupHeaderLink>
                </StyledLinkGroupHeader>

                <StyledLinkGroup>
                    {landingPageIndexes.map(i => (
                        <StyledLink key={i} href={`/listings/${i}`}>
                            {`${i} `}
                        </StyledLink>
                    ))}
                    <StyledLink href="/about/most-popular/">Custom</StyledLink>
                </StyledLinkGroup>
            </SitemapContainer>
        );
    }
}

/* 
    WrappedContainer removes the isVisible prop after it is used by the styled component.
    Fixes warning: "React does not recognize the `isVisible` prop on a DOM element"
*/
const WrappedContainer = ({ isVisible, ...props }) => (
    <PaddedContainer {...props} />
);

const SitemapContainer = styled(WrappedContainer)`
    display: ${p => (p.isVisible ? 'block' : 'none;')};
    background-color: ${p => p.theme.footer.legal.bgColor};
    color: ${p => p.theme.footer.legal.textColor};
    line-height: 2;
    padding-bottom: 30px;
    padding-top: 0;
    ${breakpoint.medium} {
        background-color: #000024;
        padding-top: 23px;
    }
`;

const StyledLinkGroup = styled.section`
    margin-bottom: 12px;
    max-width: 800px;

    ${breakpoint.medium} {
        margin-bottom: 16px;
    }
`;

const StyledLinkGroupHeader = styled.h3`
    display: block;
`;

const StyledLinkGroupHeaderLink = styled(Link)`
    color: ${white};
    font: 15px/2.6 ${FontFamily.SharpSansBold};
    border-bottom-color: transparent;
    ${breakpoint.medium} {
        color: ${p => p.theme.footer.legal.textColor};
        border-bottom-color: ${p => p.theme.footer.legal.linkUnderlineColor};
        font: 14px/1.4 ${FontFamily.SharpSansBold};
    }
`;

const StyledLink = styled(Link)`
    color: rgba(255, 255, 255, 0.6);
    border: 0;
    font: 15px/1.3 ${FontFamily.SharpSansSemibold};

    border: 0;
    margin-right: 8px;

    ${breakpoint.medium} {
        font: 14px/1.4 ${FontFamily.SharpSansBold};
        color: ${p => p.theme.footer.legal.linkColor};
        &:focus,
        &:hover {
            color: ${p => p.theme.footer.legal.linkColorHover};
        }

        &:visited {
            color: ${p => p.theme.footer.legal.linkColorVisited};
        }
    }
`;

export default withTheme(Sitemap);
